package com.fantasytenis.shared.domain.lineup.usecases

import com.fantasytenis.shared.data.lineup.repository.LineupRepository
import com.fantasytenis.shared.data.lineup.repository.LineupWithValidation
import com.fantasytenis.shared.data.lineup.repository.LineupValidationResult
import com.fantasytenis.shared.data.lineup.repository.CanAddPlayerResult
import com.fantasytenis.shared.domain.lineup.entity.LineupEntity
import com.fantasytenis.shared.domain.lineup.entity.LineupSlots
import com.fantasytenis.shared.domain.lineup.entity.Category

/**
 * Create a new lineup for a contest entry.
 */
class CreateLineupUseCase(
    private val lineupRepository: LineupRepository
) {
    suspend operator fun invoke(
        contestEntryId: String,
        contestId: String,
        slots: LineupSlots? = null
    ): Result<LineupWithValidation> {
        return lineupRepository.createLineup(contestEntryId, contestId, slots)
    }
}

/**
 * Update an existing lineup.
 */
class UpdateLineupUseCase(
    private val lineupRepository: LineupRepository
) {
    suspend operator fun invoke(
        lineupId: String,
        slots: LineupSlots
    ): Result<LineupWithValidation> {
        return lineupRepository.updateLineup(lineupId, slots)
    }
}

/**
 * Get a lineup by ID.
 */
class GetLineupUseCase(
    private val lineupRepository: LineupRepository
) {
    suspend operator fun invoke(lineupId: String): Result<LineupEntity> {
        return lineupRepository.getLineup(lineupId)
    }
}

/**
 * Get all lineups for current user.
 */
class GetUserLineupsUseCase(
    private val lineupRepository: LineupRepository
) {
    suspend operator fun invoke(contestId: String? = null): Result<List<LineupEntity>> {
        return lineupRepository.getUserLineups(contestId)
    }
}

/**
 * Validate a lineup without saving.
 */
class ValidateLineupUseCase(
    private val lineupRepository: LineupRepository
) {
    suspend operator fun invoke(
        contestId: String,
        slots: LineupSlots
    ): Result<LineupValidationResult> {
        return lineupRepository.validateLineup(contestId, slots)
    }
}

/**
 * Check if a player can be added to a slot.
 */
class CanAddPlayerUseCase(
    private val lineupRepository: LineupRepository
) {
    suspend operator fun invoke(
        lineupId: String,
        category: Category,
        playerId: String
    ): Result<CanAddPlayerResult> {
        return lineupRepository.canAddPlayer(lineupId, category, playerId)
    }
}

/**
 * Lock a lineup (finalize it).
 */
class LockLineupUseCase(
    private val lineupRepository: LineupRepository
) {
    suspend operator fun invoke(lineupId: String): Result<LineupEntity> {
        return lineupRepository.lockLineup(lineupId)
    }
}
